import { useEffect, useRef, useState, memo, useCallback } from 'react';
import { useSelector } from 'react-redux';
import classNames from 'classnames';
// import LocationLine from './DealItemExperimental/LocationLine';
import { isMobile, parseDate } from '../../utilities/helpers';
import { t } from '../../utilities/translator';
import DealFlight from './DealItemExperimental/DealFlight';
import CardRibbons from './DealItemExperimental/CardRibbons';
import format from '../../utilities/format';
// import OfferHighlights from './DealItemExperimental/OfferHighlights'; // Don't remove this, it will be used later
import { selectDepartures, selectDestinations } from '../mainFilter/selectors';
import CardTitle from './DealItemExperimental/CardTitle';
import DealSettings from './DealItemExperimental/DealSettings';
// import FavoriteDealIcon from './DealItemExperimental/FavoriteDealIcon';
import LocationIcons from './DealItem/LocationIcons';
import TrustYouScore from './DealItemExperimental/TrustYouScore';
import useDealClickHandler from '../../hooks/dealItem/useDealClickHandler';
import DealSlider from './DealItem/DealSlider';
import abTesting from '../../services/abTesting';
import DealFooter from './DealItemExperimental/DealFooter';

const DealItemExperimental = ({
  dealItem,
  position,
  bestOutOfDestination = undefined,
  hideSettings = false,
  selected = false,
  type,
}) => {
  const cardRef = useRef(null);
  const destinations = useSelector(selectDestinations);
  const departures = useSelector(selectDepartures);
  const [cardHeight, setCardHeight] = useState(518);
  const [isSettingsShown, setIsSettingsShown] = useState(false);
  const [cardTitle, setCardTitle] = useState('');
  const [sliderTutorialShown, setSliderTutorialShown] = useState(false);
  const { destinationData, offer, hotel, flight } = dealItem;
  const { name, atlas_top: countryName } = destinationData;
  const { outboundDate, stickers, hfOfferId } = offer;
  const { photos } = hotel;
  const { trustYouReviews = {} } = hotel;
  const depDate = parseDate(outboundDate);
  const dealDestination = destinations && destinations.find(destination => destination?.id === dealItem?.destinationData?.destinationId);
  const dealDeparture = departures && departures.filter(dep => dep.id === flight.city_from_airport_code);

  const handleDealClick = useDealClickHandler(dealItem, position, cardTitle, sliderTutorialShown);

  useEffect(() => {
    if (cardRef.current) {
      setCardHeight(cardRef.current.clientHeight > 0 ? cardRef.current.clientHeight : 518);
    }
  }, [])

  const handleShowSettingsClick = useCallback(() => {
    setIsSettingsShown((prev) => !prev);
  }, []);

  const getHotelRating = () => {
    return Array.from({ length: hotel.rating }, (_, index) => (
      <i key={index} className="ri-star-fill" />
    ));
  };

  return (
    <div
      className="deal-item-experimental pb-10 col-lg-3 col-md-4 col-sm-6 deal-item-bswrap"
      data-position={position + 1}
      data-hfofferid={hfOfferId}
    >
      {!hideSettings && (
        <CardTitle
          onClick={handleShowSettingsClick}
          setCardTitle={setCardTitle}
          hotel={hotel}
          dealDestination={dealDestination}
          destinations={destinations}
          bestOutOfDestination={bestOutOfDestination}
        />
      )}
      <div className={classNames("flip-card", { selected })}>
        <div className={classNames('flip-card-inner rounded-xl', { 'flipped': isSettingsShown })} style={{ height: cardHeight }}>
          {!hideSettings && (
            <DealSettings
              height={cardHeight}
              dealDestination={dealDestination}
              onClose={handleShowSettingsClick}
              owSettingsBtn={isSettingsShown}
              hideSettings={hideSettings}
              dealItem={dealItem}
            />
          )}
          <div className="card-face front rounded-xl shadow-lg relative cursor-pointer" ref={cardRef} onClick={handleDealClick}>
            <div className="gallery relative">
              <div className="slider overflow-hidden rounded-t-xl h-[200px]">
                <DealSlider
                  photos={photos}
                  index={position}
                  dealItem={dealItem}
                  setTutorialVisible={setSliderTutorialShown}
                />
              </div>
              <CardRibbons data={stickers} specialDealLabel={hotel.specialDealLabel} dealItem={dealItem} />

              <LocationIcons
                showSettingsBtn={isSettingsShown}
                onSettingsClick={handleShowSettingsClick}
                hideSettings={hideSettings}
                dealItem={dealItem}
                type={type}
                position={position}
              />

              <div className="absolute-fix absolute bottom-0 p-3 flex flex-col text-white text-start">
                <span className="heading-tiny leading-none !font-medium pb-1">{countryName}</span>
                <span className="text-xl leading-none !font-bold pb-1">{name}</span>
              </div>
            </div>
            <div className="m-details p-4">
              <div className="flex justify-between gap-1 items-center text-nowrap">
                <div className={classNames("heading-small leading-4 text-start", { 'text-[14px]': (abTesting.flexibleDates && !isMobile()) })}>{format(depDate, 'd MMM').replace('.','')} · {offer.duration} {t("dealItem.nights_plural", { count: offer.duration })}</div>
                {(abTesting.flexibleDates && window.location.href.indexOf('b2bcustomerview=true') === -1) &&
                  <div className="flexible-dates-link cursor-pointer flex gap-1 items-center">
                    <span className="fdl-1 text-xs font-medium leading-none">{t('dealItem.flexibleDates')}</span>
                    <i className="ri-arrow-right-s-line fdl-2" />
                  </div>
                }
              </div>
              <div className="hotel-highlight-txt pt-2 text-[12px] text-shark-500 font-medium">
                {hotel.packageHighlightOnCard}
              </div>
            </div>
            <div className="flex flex-col gap-4 p-4 min-h-[156px]">
              <div className="flex flex-col gap-3 min-h-[80px]">
                <div className="flex flex-col gap-1">
                  <div className="deal-rating flex text-xs">
                    {getHotelRating()}
                  </div>
                  <div className="hotel-name leading-5 label-large md:whitespace-nowrap text-ellipsis overflow-hidden text-start font-semibold">{hotel.name}</div>
                </div>
                <TrustYouScore trustYouReviews={trustYouReviews} />
              </div>
              <DealFlight
                flight={flight}
                nightFlight={stickers?.nightFlight}
                stickers={stickers}
                dealDeparture={dealDeparture}
              />
            </div>
            <DealFooter offer={offer} stickers={stickers} type={type} />
          </div>
        </div>
      </div>
    </div>
  )
}

export default memo(DealItemExperimental)
